// @ts-check

/**
 * @file Renders the reusable sign-in form.
 */

import {FlashNotifications} from "flash-notifications"
import {Form, FormInputs} from "@kaspernj/api-maker/build/form"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, ScrollView, Text, View} from "react-native"
import {signIn} from "../shared/auth"
import errorMessage from "../shared/error-message"
import FormTextInput from "../components/form/form-text-input"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} SignInFormProps
 * @property {(...args: unknown[]) => unknown} [onSignInSuccess] Callback after successful sign-in.
 */

/**
 * @typedef {object} SignInFormState
 * @property {boolean} rememberMe Whether the user wants the longer "remember me" session lifetime.
 * @property {boolean} submitting Whether sign-in is submitting.
 */

/**
 * Reusable sign-in form. Used both by the /users/sign-in route and
 * by BoardLayout as an inline replacement for route children when
 * no user is signed in — so unauthenticated access to any route
 * shows the form in place instead of bouncing the user via
 * router.replace.
 * @augments {ShapeComponent<SignInFormProps, SignInFormState>}
 */
class SignInForm extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = propTypesExact({
    onSignInSuccess: PropTypes.func
  })

  static defaultProps = {
    onSignInSuccess: undefined
  }

  form = new FormInputs({})
  /** @type {SignInFormState} */
  state = {
    rememberMe: false,
    submitting: false
  }

  /** Initializes translations for the sign-in form. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Submits the sign-in form.
   * @returns {Promise<void>} Resolves when sign-in handling completes.
   * @rejects {unknown} When sign-in fails.
   */
  onSubmit = async () => {
    const values = /** @type {Record<string, unknown>} */ (this.tt.form.asObject())
    const email = typeof values.email === "string" ? values.email.trim() : ""
    const password = typeof values.password === "string" ? values.password : ""
    if (!email || !password) {
      FlashNotifications.error(_("Email and password are required"))
      return
    }
    this.s.submitting = true
    try {
      await signIn(email, password, {rememberMe: this.s.rememberMe})
      FlashNotifications.success(_("Signed in"))
      const onSignInSuccess = this.p.onSignInSuccess
      if (onSignInSuccess) onSignInSuccess()
    } catch (error) {
      FlashNotifications.error(errorMessage(error) || _("Sign in failed"))
      this.s.submitting = false
      throw error
    }
  }

  /** Toggles the Remember Me checkbox. */
  onRememberMePress = () => {
    this.s.rememberMe = !this.s.rememberMe
  }

  /**
   * Renders sign-in form fields.
   * @returns {ReactNode} Sign-in form content.
   */
  render() {
    const {rememberMe, submitting} = this.s
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <ScrollView
        style={styles[`screen-${themeName}`] ||= {
          backgroundColor: colors.screen,
          flex: 1
        }}
        testID="signInScreen"
      >
        <View
          style={styles.content ||= {
            alignItems: "center",
            padding: 32
          }}
          testID="signInScreen/content"
        >
          <Form
            form={this.tt.form}
            onSubmit={this.tt.onSubmit}
          >
            <View
              style={styles[`card-${themeName}`] ||= {
                backgroundColor: colors.surface,
                borderColor: colors.border,
                borderRadius: 14,
                borderWidth: 1,
                gap: 14,
                maxWidth: 420,
                padding: 24,
                width: "100%"
              }}
              testID="signInScreen/card"
            >
              <Text
                style={styles[`title-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 24,
                  fontWeight: 700
                }}
                testID="signInScreen/title"
              >
                {_("Sign in")}
              </Text>
              <Text
                style={styles[`subtitle-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 14,
                  fontWeight: 500
                }}
                testID="signInScreen/subtitle"
              >
                {_("Enter your email and password to continue")}
              </Text>
              <FormTextInput
                label={_("Email")}
                name="email"
                testID="signInScreen/emailInput"
              />
              <FormTextInput
                label={_("Password")}
                name="password"
                secureTextEntry
                testID="signInScreen/passwordInput"
              />
              <Pressable
                onPress={this.tt.onRememberMePress}
                style={styles.rememberMeRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  gap: 10
                }}
                testID="signInScreen/rememberMe"
              >
                <View
                  style={styles[`rememberMeBox-${themeName}-${rememberMe}`] ||= {
                    alignItems: "center",
                    backgroundColor: rememberMe ? colors.primary : colors.surface,
                    borderColor: rememberMe ? colors.primary : colors.borderStrong,
                    borderRadius: 4,
                    borderWidth: 1,
                    height: 22,
                    justifyContent: "center",
                    width: 22
                  }}
                  testID="signInScreen/rememberMe/box"
                >
                <View
                  style={styles.rememberMeMark ||= {
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  testID="signInScreen/rememberMe/mark"
                >
                  {rememberMe &&
                    <View testID="signInScreen/rememberMe/mark/icon">
                      <FontAwesome6 color={colors.onPrimary} name="check" size={12} />
                    </View>
                  }
                </View>
                </View>
                <Text
                  style={styles[`rememberMeLabel-${themeName}`] ||= {
                    color: colors.text,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="signInScreen/rememberMe/label"
                >
                  {_("Remember me")}
                </Text>
              </Pressable>
              <Pressable
                disabled={submitting}
                onPress={this.tt.onSubmit}
                style={styles[`submitButton-${themeName}-${submitting}`] ||= {
                  alignItems: "center",
                  backgroundColor: submitting ? colors.disabledBackground : colors.primary,
                  borderRadius: 10,
                  flexDirection: "row",
                  justifyContent: "center",
                  marginTop: 4,
                  paddingVertical: 12
                }}
                testID="signInScreen/submitButton"
              >
                <View
                  style={styles.submitButtonIcon ||= {
                    marginRight: 8,
                    width: 14
                  }}
                  testID="signInScreen/submitButton/icon"
                >
                  <FontAwesome6
                    color={submitting ? colors.disabledText : colors.onPrimary}
                    name="right-to-bracket"
                    size={14}
                  />
                </View>
                <Text
                  style={styles[`submitButtonText-${themeName}-${submitting}`] ||= {
                    color: submitting ? colors.disabledText : colors.onPrimary,
                    fontSize: 14,
                    fontWeight: 700
                  }}
                  testID="signInScreen/submitButton/text"
                >
                  {submitting ? _("Signing in...") : _("Sign in")}
                </Text>
              </Pressable>
            </View>
          </Form>
        </View>
      </ScrollView>
    )
  }
}

/** @type {NamedExoticComponent<SignInFormProps>} */
const SignInFormComponent = memo(shapeComponent(SignInForm))

export default SignInFormComponent
